import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getCollectionChangeStream } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { getCurrentUserServer } from '@adornis/bh-util/util.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { type Observable, bufferTime, concatMap, filter, of, startWith, switchMap } from 'rxjs';
import { bhBaseQlContext } from '../bh-context.js';
import { Participation } from '../entities/Participation.js';
import { BHFilter } from '../entities/bh-filter.js';
import { Permission } from '../enums/roles.js';
import { mongo } from '../server/mongo.js';
import { UserDataview } from '../use-case-entities/UserDataview.js';
import { withIdTiebreaker } from '../util.js';

// Every write used to re-run every admin tab; filter by institution for inserts and replaces,
// but always re-run for every other change type, whose events carry no institution.
function watchParticipationsMaterializedForInstitution(institutionID: string): Observable<unknown> {
  return getCollectionChangeStream('participations-materialized').pipe(
    bufferTime(500),
    filter(changes => changes.length > 0),
    concatMap(async changes => {
      if (changes.some(change => change.operationType !== 'insert' && change.operationType !== 'replace')) return true;

      const ids = [
        ...new Set(
          changes.flatMap(change =>
            change.operationType === 'insert' || change.operationType === 'replace' ? [change.documentKey._id] : [],
          ),
        ),
      ];
      const document = await mongo()
        .collection('participations-materialized')
        .raw.findOne({ _id: { $in: ids }, 'product.institutionID': institutionID }, { projection: { _id: 1 } });
      return !!document;
    }),
    filter(Boolean),
    startWith(true),
  );
}

export const subscribeUserDataview = registerSubscription({
  operationName: 'subscribeUserDataview',
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],
  type: () => [UserDataview],
  resolve: (skip: number, limit: number, filter: BHFilter) => (gqlFields: BaseQLSelectionSet<UserDataview>) => {
    if (!filter.institutionID) return of([]);
    const institutionID = filter.institutionID;

    return watchParticipationsMaterializedForInstitution(institutionID).pipe(
      switchMap(async () => {
        const currentUser = await getCurrentUserServer();
        if (!currentUser?.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_USERS))
          throw new ExpectedError('no permission to do that');

        bhBaseQlContext.institutionID = institutionID;

        if (gqlFields.user?.suspended) {
          gqlFields.user.suspentions = gqlFields.user.suspentions ?? {};
          gqlFields.user.suspentions.institutionID = 1;
        }

        const filter2 = {
          'product.institutionID': institutionID,
          ...(filter.timeframeID ? { 'product.timeframeID': filter.timeframeID } : {}),
        };

        const institutionSuspension = {
          $gt: [
            {
              $size: {
                $filter: {
                  input: { $ifNull: ['$user.suspentions', []] },
                  as: 'suspension',
                  cond: {
                    $eq: ['$$suspension.institutionID', institutionID],
                  },
                },
              },
            },
            0,
          ],
        };

        // Main aggregation pipeline
        const aggregation = [
          // { $lookup: lookupEntityById(Product, 'productID', 'product') },
          // { $set: { product: { $first: '$product' } } },
          // { $lookup: lookupEntityById(User, 'userID', 'user') },
          // { $set: { user: { $first: '$user' } } },
          { $match: filter2 },
          { $set: { _participation: '$$ROOT' } },
          { $unset: ['_participation.user', '_participation.userStammdaten'] },
          {
            $group: {
              _id: '$userID',
              participations: { $push: '$_participation' },
              user: { $first: '$user' },
              userStammdaten: { $first: '$userStammdaten' },
            },
          },
          {
            $set: {
              institutionID: { $first: '$participations.product.institutionID' },
              timeframeID: { $first: '$participations.product.timeframeID' },
              'user.suspended': { $cond: { if: institutionSuspension, then: true, else: false } },
              suspension: {
                $ifNull: [
                  {
                    $first: {
                      $filter: {
                        input: { $ifNull: ['$user.suspentions', []] },
                        as: 'suspension',
                        cond: { $eq: ['$$suspension.institutionID', institutionID] },
                      },
                    },
                  },
                  null,
                ],
              },
            },
          },
          { $match: filter.getMongoFilter() },
          { $addFields: { _class: UserDataview._class } },
          { $project: gqlFields },
          // Nested inclusion projections omit scalar nulls; restore the requested suspension field.
          ...(gqlFields.suspension ? [{ $set: { suspension: { $ifNull: ['$suspension', null] } } }] : []),
          { $sort: withIdTiebreaker(filter.getMongoOptions().sort) },
          { $skip: skip },
          { $limit: limit },
        ];

        const collection = mongo().collection<Participation>('participations-materialized');
        const result = await collection.raw
          .aggregate<UserDataview>(aggregation, { collation: { locale: 'de' } })
          .toArray();

        return result;
      }),
    );
  },
});

export const subscribeUserDataviewCount = registerSubscription({
  operationName: 'subscribeUserDataviewCount',
  type: () => Int,
  params: [{ name: 'filter', type: () => BHFilter }],

  resolve: (filter: BHFilter) => () => {
    if (!filter.institutionID) return of(0);
    const institutionID = filter.institutionID;

    return watchParticipationsMaterializedForInstitution(institutionID).pipe(
      switchMap(async () => {
        const currentUser = await getCurrentUserServer();
        if (!currentUser?.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_USERS))
          throw new ExpectedError('no permission to do that');

        bhBaseQlContext.institutionID = institutionID;

        const institutionSuspension = {
          $gt: [
            {
              $size: {
                $filter: {
                  input: { $ifNull: ['$user.suspentions', []] },
                  as: 'suspension',
                  cond: { $eq: ['$$suspension.institutionID', institutionID] },
                },
              },
            },
            0,
          ],
        };

        const aggregation = [
          {
            $match: {
              'product.institutionID': institutionID,
              ...(filter.timeframeID ? { 'product.timeframeID': filter.timeframeID } : {}),
            },
          },
          { $set: { _participation: '$$ROOT' } },
          { $unset: ['_participation.user', '_participation.userStammdaten'] },
          {
            $group: {
              _id: '$userID',
              participations: { $push: '$_participation' },
              user: { $first: '$user' },
              userStammdaten: { $first: '$userStammdaten' },
            },
          },
          {
            $set: {
              institutionID: { $first: '$participations.product.institutionID' },
              timeframeID: { $first: '$participations.product.timeframeID' },
              'user.suspended': { $cond: { if: institutionSuspension, then: true, else: false } },
              suspension: {
                $ifNull: [
                  {
                    $first: {
                      $filter: {
                        input: { $ifNull: ['$user.suspentions', []] },
                        as: 'suspension',
                        cond: { $eq: ['$$suspension.institutionID', institutionID] },
                      },
                    },
                  },
                  null,
                ],
              },
            },
          },
          { $match: filter.getMongoFilter() },
          { $count: 'count' },
        ];

        const collection = mongo().collection<Participation>('participations-materialized');
        const result = await collection.raw
          .aggregate<{ count: number }>(aggregation, { collation: { locale: 'de' } })
          .toArray();
        return result[0]?.count ?? 0;
      }),
    );
  },
});
